import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createVNode as _createVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { class: "data-summery-root" }
const _hoisted_2 = ["onMouseover", "onMouseleave"]
const _hoisted_3 = ["onMouseover", "onMouseleave"]

import { computed, PropType, watch, ref, onMounted, onUnmounted } from 'vue'
import { camelCase } from 'lodash'
import $ from 'jquery'
import RainPanel from '@/components/top/summary_panels/rain/RainPanel.vue'
import WaterLevelSummaryPanel from '@/components/top/summary_panels/water/WaterLevelSummaryPanel.vue'
import LegendSummaryPanel from '@/components/top/summary_panels/legend/LegendSummaryPanel.vue'
import MapPanel from '@/components/top/summary_panels/map/MapPanel.vue'
import CameraPanel from '@/components/top/summary_panels/camera/CameraPanel.vue'
import StationListPanel from '@/components/top/summary_panels/list/StationListPanel.vue'
import {
  DefinitionInfoSummaryItemCommon,
  DefinitionInfoSummaryItemMap,
  DefinitionInfoSummaryItemLegend,
  DefinitionInfoSummaryItemWaterLevel,
  DefinitionInfoSummaryItemCamera,
} from '@/models/initData/DefinitionInfo'
import { useInitDataStore } from '@/stores/initDataStore'
import { useGlobalStore } from '@/stores/globalStore'

type Area = {
  event: {
    args: {
      args: {
        summaryId: string
      }[]
    }
  }[]
}

type Cell = {
  row: number
  column: number
  size: {
    x: number
    y: number
  }
}


export default /*#__PURE__*/_defineComponent({
  props: {
  area: {
    type: Object as PropType<Area>,
  },
},
  setup(__props) {

const props = __props


const COLUMN_NUM = 6
const MIDDLE_WIDTH_TH = 1400
const BIG_WIDTH_TH = 1920
const initDataStore = useInitDataStore()
const globalStore = useGlobalStore()
const windowWidth = ref(window.innerWidth)

onMounted(async () => {
  window.addEventListener('resize', handleResize)
  handleResize()
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})

const handleResize = () => {
  windowWidth.value = window.innerWidth
}

const getPosition = (item: DefinitionInfoSummaryItemCommon) => {
  const { position } = item

  return {
    row: Math.ceil(position / COLUMN_NUM),
    column: ((position - 1) % COLUMN_NUM) + 1,
  }
}

const summaryId = computed(() => {
  if (!props.area?.event || !props.area?.event[0]) {
    return
  }

  if (!props.area?.event[0].args?.args) {
    return
  }

  return camelCase(props.area?.event[0].args.args[0].summaryId)
})

const summaryItems = computed(() => {
  return initDataStore.definitionInfoTop(summaryId.value)?.items || []
})

const commonTypeCells = computed(() => {
  const commonTypes = ['commonType1', 'commonType2']

  return summaryItems.value
    .filter(item => commonTypes.includes(camelCase(item.panelType)))
    .map(item => {
      return {
        ...getPosition(item as DefinitionInfoSummaryItemCommon),
        item: item as DefinitionInfoSummaryItemCommon,
      }
    })
})

const waterLevelTypeCells = computed(() => {
  return summaryItems.value
    .filter(item => camelCase(item.panelType) === 'waterLevelType1')
    .map(item => {
      return {
        ...getPosition(item as DefinitionInfoSummaryItemCommon),
        item: item as DefinitionInfoSummaryItemWaterLevel,
      }
    })
})

const legendTypeCells = computed(() => {
  return summaryItems.value
    .filter(item => camelCase(item.panelType) === 'commonLegendType1')
    .map(item => {
      return {
        ...getPosition(item as DefinitionInfoSummaryItemCommon),
        item: item as DefinitionInfoSummaryItemLegend,
      }
    })
})

const mapTypeCells = computed(() => {
  if (globalStore.isSP) {
    return []
  }

  return summaryItems.value
    .filter(item => camelCase(item.panelType) === 'mapType1')
    .map(item => {
      return {
        ...getPosition(item as DefinitionInfoSummaryItemCommon),
        size: { x: 4, y: 6 },
        item: item as DefinitionInfoSummaryItemMap,
      }
    })
})

const listTypeCells = computed(() => {
  return summaryItems.value
    .filter(item => camelCase(item.panelType) === 'listType1')
    .map(item => {
      return {
        ...getPosition(item as DefinitionInfoSummaryItemCommon),
        size: { x: 2, y: 6 },
        item,
      }
    })
})

const cameraTypeCells = computed(() => {
  return summaryItems.value
    .filter(item => camelCase(item.panelType) === 'cameraType1')
    .map(item => {
      return {
        ...getPosition(item as DefinitionInfoSummaryItemCommon),
        item: item as DefinitionInfoSummaryItemCamera,
      }
    })
})

const maxRows = computed(() => {
  if (summaryItems.value.length === 0) {
    return 0
  }

  const lastCell = summaryItems.value[summaryItems.value.length - 1]
  const row = Math.ceil(lastCell.position / COLUMN_NUM)

  return row + lastCell.args.size?.y || 6
})

const containerStyle = computed(() => {
  const w = windowWidth.value < MIDDLE_WIDTH_TH ? 218 : 218 + (windowWidth.value - 1366) / 6 - 4
  const h = windowWidth.value < BIG_WIDTH_TH ? 87 : 136
  const rows = `grid-template-rows: repeat(${maxRows.value}, ${h}px)`
  const columns = `grid-template-columns: repeat(6, ${w}px)`

  return [rows, columns].join(';')
})

const containerClass = computed(() => {
  return windowWidth.value < BIG_WIDTH_TH ? '' : 'container-big'
})

const cellClass = (cell: Cell) => {
  return `grid-item-row-${cell.row} grid-item-column-${cell.column} x-${cell.column + cell.size.x} y-${
    cell.row + cell.size.y
  }`
}

// SP時のセルのサイズ調整
const commonTypeCellStyle = (cell: DefinitionInfoSummaryItemCommon) => {
  if (!globalStore.isSP) {
    return {}
  }

  const elementsSize = cell.args.element.length
  const updateTimeHeight = camelCase(cell.panelType) === 'commonType2' ? 35 : 0

  return {
    height: `${elementsSize * 50 + 25 + updateTimeHeight}px`,
  }
}

const handleMouseonCell = (cell: DefinitionInfoSummaryItemCommon, e: MouseEvent) => {
  const $popup = $(`#popup-${cell.args.no}`)
  const scrollTop = $('html').scrollTop() || 0
  const margin = 10
  const width = $popup.width() || 0
  let left = e.pageX + margin
  const top = e.pageY - scrollTop - margin

  if (left + width > window.innerWidth) {
    left = window.innerWidth - width - margin
  }

  $popup.addClass('active')
  $popup.css({
    left: `${left}px`,
    top: `${top}px`,
  })
}

const handleMouseleaveCell = (cell: DefinitionInfoSummaryItemCommon) => {
  $(`#popup-${cell.args.no}`).removeClass('active')
}

watch(
  () => props.area,
  () => window.scrollTo(0, 0)
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      style: _normalizeStyle(_unref(containerStyle)),
      class: _normalizeClass([_unref(containerClass), "grid-container"])
    }, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(mapTypeCells), (cell, index) => {
        return (_openBlock(), _createElementBlock("div", {
          key: index,
          class: _normalizeClass([cellClass(cell), "grid-item map-type-cell"])
        }, [
          _createVNode(MapPanel, {
            cell: cell.item
          }, null, 8, ["cell"])
        ], 2))
      }), 128)),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(listTypeCells), (cell, index) => {
        return (_openBlock(), _createElementBlock("div", {
          key: index,
          class: _normalizeClass([cellClass(cell), "grid-item list-type-cell"])
        }, [
          _createVNode(StationListPanel, {
            cell: cell.item
          }, null, 8, ["cell"])
        ], 2))
      }), 128)),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(commonTypeCells), (cell) => {
        return (_openBlock(), _createElementBlock("div", {
          onMouseover: ($event: any) => (handleMouseonCell(cell.item, $event)),
          onMouseleave: ($event: any) => (handleMouseleaveCell(cell.item)),
          key: cell.item.args.no,
          style: _normalizeStyle(commonTypeCellStyle(cell.item)),
          class: _normalizeClass([`grid-item-row-${cell.row} grid-item-column-${cell.column} x-${cell.column + cell.item.args.size.x} y-${
          cell.row + cell.item.args.size.y
        }`, "grid-item"])
        }, [
          _createVNode(RainPanel, {
            cell: cell.item
          }, null, 8, ["cell"])
        ], 46, _hoisted_2))
      }), 128)),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(waterLevelTypeCells), (cell) => {
        return (_openBlock(), _createElementBlock("div", {
          onMouseover: ($event: any) => (handleMouseonCell(cell.item, $event)),
          onMouseleave: ($event: any) => (handleMouseleaveCell(cell.item)),
          key: cell.item.args.no,
          class: _normalizeClass([`grid-item-row-${cell.row} grid-item-column-${cell.column} x-${cell.column + cell.item.args.size.x} y-${
          cell.row + cell.item.args.size.y
        }`, "grid-item grid-item-water-level"])
        }, [
          _createVNode(WaterLevelSummaryPanel, {
            cell: cell.item
          }, null, 8, ["cell"])
        ], 42, _hoisted_3))
      }), 128)),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(legendTypeCells), (cell, index) => {
        return (_openBlock(), _createElementBlock("div", {
          key: index,
          class: _normalizeClass([`grid-item-row-${cell.row} grid-item-column-${cell.column} x-${cell.column + cell.item.args.size.x} y-${
          cell.row + cell.item.args.size.y
        }`, "grid-item grid-item-legend"])
        }, [
          _createVNode(LegendSummaryPanel, {
            url: cell.item.args?.path
          }, null, 8, ["url"])
        ], 2))
      }), 128)),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(cameraTypeCells), (cell, index) => {
        return (_openBlock(), _createElementBlock("div", {
          key: index,
          class: _normalizeClass([`grid-item-row-${cell.row} grid-item-column-${cell.column} x-${cell.column + cell.item.args.size.x} y-${
          cell.row + cell.item.args.size.y
        }`, "grid-item grid-item-camera"])
        }, [
          _createVNode(CameraPanel, {
            cell: cell.item
          }, null, 8, ["cell"])
        ], 2))
      }), 128))
    ], 6)
  ]))
}
}

})